<template>
  <div class="align-items-center justify-content-center" style="display: flex">
    <div style="width: 100%; max-width: 1100px; min-width: 320px">
      <h2 style="text-align: center; margin-bottom: 12px">
        Spielorte zusammenführen
      </h2>

      <Message severity="info" :closable="false" style="margin-bottom: 16px">
        Wähle den zu behaltenden Spielort und den doppelt erfassten Spielort.
        Der doppelt erfasste wird gelöscht; alle referenzierenden Mannschaften,
        Spiele, Spieltage und Vor-Saison-Anmeldungen werden auf den zu
        behaltenden umgehängt. Einzelne Angaben (Board-Anzahl, Raucher-Regelung,
        …) lassen sich vorher in den behaltenen Spielort übernehmen.
      </Message>

      <h4 v-if="error && error.length > 0" style="color: red">{{ error }}</h4>

      <div
        style="margin-bottom: 16px; border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden"
      >
        <div
          style="display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #eef1f5; cursor: pointer"
          @click="showDuplicates = !showDuplicates"
        >
          <i :class="showDuplicates ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" />
          <strong>Mögliche Dubletten</strong>
          <span
            v-if="!loadingDuplicates"
            style="background: #b3261e; color: #fff; border-radius: 10px; padding: 0 8px; font-size: 0.8em"
          >{{ duplicates.length }}</span>
          <i v-if="loadingDuplicates" class="pi pi-spin pi-spinner" />
          <span style="flex: 1" />
          <Button
            label="Neu suchen"
            icon="pi pi-refresh"
            severity="secondary"
            size="small"
            :disabled="loadingDuplicates || busy"
            @click.stop="loadDuplicates"
          />
        </div>
        <div v-if="showDuplicates" style="padding: 12px">
          <div v-if="loadingDuplicates">
            Suche nach ähnlichen Adressen &nbsp;<i class="pi pi-spin pi-spinner" />
          </div>
          <div
            v-else-if="duplicates.length === 0"
            style="color: #6c757d; font-style: italic"
          >
            Keine möglichen Dubletten gefunden.
          </div>
          <table v-else style="width: 100%; border-collapse: collapse">
            <thead>
              <tr style="text-align: left; border-bottom: 2px solid #dee2e6">
                <th style="padding: 6px">Behalten (Vorschlag)</th>
                <th style="padding: 6px">Löschen (Vorschlag)</th>
                <th style="padding: 6px; white-space: nowrap">Ähnlichkeit</th>
                <th style="padding: 6px"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in duplicateRows"
                :key="idx"
                style="border-bottom: 1px solid #eef1f5; vertical-align: top"
              >
                <td style="padding: 6px">
                  <div v-if="row.keep">
                    <strong>{{ row.keep.name }}</strong>
                    <div style="font-size: 0.85em; color: #6c757d">
                      {{ row.keep.street }}<span
                        v-if="row.keep.street && (row.keep.plz || row.keep.city)"
                      > · </span>{{ row.keep.plz }} {{ row.keep.city }}
                    </div>
                  </div>
                  <div v-else style="color: #6c757d; font-style: italic">unbekannt</div>
                </td>
                <td style="padding: 6px">
                  <div v-if="row.remove">
                    <strong>{{ row.remove.name }}</strong>
                    <div style="font-size: 0.85em; color: #6c757d">
                      {{ row.remove.street }}<span
                        v-if="row.remove.street && (row.remove.plz || row.remove.city)"
                      > · </span>{{ row.remove.plz }} {{ row.remove.city }}
                    </div>
                  </div>
                  <div v-else style="color: #6c757d; font-style: italic">unbekannt</div>
                </td>
                <td style="padding: 6px; white-space: nowrap">
                  <span
                    :style="scoreBadgeStyle(row.pair.score)"
                    style="border-radius: 10px; padding: 1px 8px; color: #fff; font-size: 0.85em"
                  >{{ row.pair.score }} %</span>
                  <div
                    v-for="(reason, ri) in row.pair.reasons"
                    :key="ri"
                    style="font-size: 0.78em; color: #6c757d; margin-top: 2px"
                  >
                    {{ reason }}
                  </div>
                </td>
                <td style="padding: 6px; white-space: nowrap">
                  <Button
                    label="Übernehmen"
                    icon="pi pi-arrow-down"
                    size="small"
                    :disabled="busy"
                    @click="applyDuplicate(row.pair)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="grid">
        <div class="col-12 md:col-6">
          <h3 style="margin-bottom: 6px">Behalten</h3>
          <Dropdown
            v-model="targetLocation"
            :options="targetOptions"
            optionLabel="representation"
            dataKey="id"
            :filter="true"
            :filterFields="filterFields"
            placeholder="Spielort wählen"
            style="width: 100%"
            :disabled="busy"
          >
            <template #option="slotProps">
              <div>
                <strong>{{ slotProps.option.name }}</strong>
                <div style="font-size: 0.85em; color: #6c757d">
                  {{ slotProps.option.street }} ·
                  {{ slotProps.option.plz }} {{ slotProps.option.city }}
                </div>
              </div>
            </template>
          </Dropdown>
          <div
            v-if="targetLocation && targetLocation.id"
            style="margin-top: 8px; padding: 10px; border: 1px solid #dee2e6; border-radius: 6px; background: #fff"
          >
            <div><strong>{{ targetLocation.name }}</strong></div>
            <div>{{ targetLocation.street }}</div>
            <div>{{ targetLocation.plz }} {{ targetLocation.city }}</div>
            <div v-if="targetLocation.numberOfBoards != null">
              {{ targetLocation.numberOfBoards }} Board(s)
            </div>
            <div v-if="targetLocation.smokingType">
              {{ smokingLabel(targetLocation.smokingType) }}
            </div>
            <div v-if="targetLocation.youthAllowed != null">
              Jugend erlaubt: {{ targetLocation.youthAllowed ? "Ja" : "Nein" }}
            </div>
          </div>
          <div v-else style="margin-top: 8px; color: #6c757d; font-style: italic">
            Noch kein Spielort gewählt
          </div>
        </div>
        <div class="col-12 md:col-6">
          <h3 style="margin-bottom: 6px; color: #b3261e">
            Doppelt erfasst (wird gelöscht)
          </h3>
          <Dropdown
            v-model="sourceLocation"
            :options="sourceOptions"
            optionLabel="representation"
            dataKey="id"
            :filter="true"
            :filterFields="filterFields"
            placeholder="Spielort wählen"
            style="width: 100%"
            :disabled="busy"
          >
            <template #option="slotProps">
              <div>
                <strong>{{ slotProps.option.name }}</strong>
                <div style="font-size: 0.85em; color: #6c757d">
                  {{ slotProps.option.street }} ·
                  {{ slotProps.option.plz }} {{ slotProps.option.city }}
                </div>
              </div>
            </template>
          </Dropdown>
          <div
            v-if="sourceLocation && sourceLocation.id"
            style="margin-top: 8px; padding: 10px; border: 1px solid #dee2e6; border-radius: 6px; background: #fff"
          >
            <div><strong>{{ sourceLocation.name }}</strong></div>
            <div>{{ sourceLocation.street }}</div>
            <div>{{ sourceLocation.plz }} {{ sourceLocation.city }}</div>
            <div v-if="sourceLocation.numberOfBoards != null">
              {{ sourceLocation.numberOfBoards }} Board(s)
            </div>
            <div v-if="sourceLocation.smokingType">
              {{ smokingLabel(sourceLocation.smokingType) }}
            </div>
            <div v-if="sourceLocation.youthAllowed != null">
              Jugend erlaubt: {{ sourceLocation.youthAllowed ? "Ja" : "Nein" }}
            </div>
          </div>
          <div v-else style="margin-top: 8px; color: #6c757d; font-style: italic">
            Noch kein Spielort gewählt
          </div>
        </div>
      </div>

      <div class="flex justify-content-center" style="margin-top: 12px">
        <Button
          label="Behalten und Löschen tauschen"
          icon="pi pi-arrows-h"
          severity="secondary"
          :disabled="(!sourceLocation && !targetLocation) || busy"
          @click="swapLocations"
        />
      </div>

      <div
        v-if="sourceLocation && targetLocation"
        style="margin-top: 20px; border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden"
      >
        <div style="padding: 10px 12px; background: #eef1f5">
          <strong>Angaben übernehmen</strong>
          <div style="font-size: 0.85em; color: #6c757d; margin-top: 2px">
            Angehakte Werte werden vom zu löschenden in den behaltenen Spielort
            übertragen. Fehlende Angaben sind vorausgewählt, abweichende nicht.
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse">
          <thead>
            <tr style="text-align: left; border-bottom: 2px solid #dee2e6">
              <th style="padding: 6px 12px">Feld</th>
              <th style="padding: 6px 12px">Behalten</th>
              <th style="padding: 6px 12px; color: #b3261e">Wird gelöscht</th>
              <th style="padding: 6px 12px; white-space: nowrap">Übernehmen</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in fieldRows"
              :key="row.key"
              style="border-bottom: 1px solid #eef1f5; vertical-align: top"
              :style="row.conflict ? 'background: #fff8e6' : ''"
            >
              <td style="padding: 6px 12px">{{ row.label }}</td>
              <td
                style="padding: 6px 12px"
                :style="row.targetEmpty ? 'color: #6c757d; font-style: italic' : ''"
              >
                {{ row.targetEmpty ? "—" : row.targetText }}
              </td>
              <td
                style="padding: 6px 12px"
                :style="row.sourceEmpty ? 'color: #6c757d; font-style: italic' : ''"
              >
                {{ row.sourceEmpty ? "—" : row.sourceText }}
              </td>
              <td style="padding: 6px 12px; white-space: nowrap">
                <Checkbox
                  v-if="row.transferable"
                  v-model="fieldsFromSource"
                  :value="row.key"
                  :inputId="'transfer-' + row.key"
                  :disabled="busy"
                />
                <label
                  v-if="row.transferable && row.conflict"
                  :for="'transfer-' + row.key"
                  style="margin-left: 6px; font-size: 0.8em; color: #b06000; cursor: pointer"
                >überschreibt</label>
                <span
                  v-if="!row.transferable"
                  style="color: #6c757d; font-size: 0.8em"
                >{{ row.sourceEmpty ? "kein Wert" : "identisch" }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="sourceLocation && targetLocation"
        style="margin-top: 16px; padding: 10px; background: #f8f9fa; border-radius: 6px"
      >
        <div v-if="loadingCounts">
          Lade Auswirkung &nbsp;<i class="pi pi-spin pi-spinner" />
        </div>
        <div v-else-if="referenceCounts">
          Diese Aktion verschiebt:
          <ul style="margin: 6px 0 0 16px">
            <li>{{ referenceCounts.teams }} Mannschaft(en)</li>
            <li>{{ referenceCounts.games }} Spiel(e)</li>
            <li>{{ referenceCounts.gameDays }} Spieltag(e)</li>
            <li>{{ referenceCounts.preSeasonTeams }} Vor-Saison-Anmeldung(en)</li>
            <li>
              und entfernt {{ referenceCounts.locationDistances }}
              Distanz-Cache-Eintrag/-Einträge (werden neu berechnet)
            </li>
            <li v-if="coordinatesSelected">
              zusätzlich werden die Distanz-Cache-Einträge des behaltenen
              Spielorts verworfen, da sich seine Koordinaten ändern
            </li>
          </ul>
        </div>
      </div>

      <div class="flex justify-content-center" style="margin-top: 20px; gap: 10px">
        <Button
          label="Zurücksetzen"
          severity="secondary"
          icon="pi pi-undo"
          :disabled="busy"
          @click="reset"
        />
        <Button
          label="Zusammenführen"
          icon="pi pi-link"
          severity="danger"
          :disabled="!canMerge || busy"
          @click="confirmDialog = true"
        />
      </div>

      <Dialog v-model:visible="confirmDialog" :closable="!busy" :modal="true">
        <template #header>
          <h3 style="margin: 0">Zusammenführen bestätigen</h3>
        </template>
        <div v-if="sourceLocation && targetLocation">
          <p>
            Der Spielort
            <strong>„{{ sourceLocation.name }}"</strong>
            wird unwiderruflich gelöscht. Alle Referenzen werden auf
            <strong>„{{ targetLocation.name }}"</strong> umgehängt.
          </p>
          <p v-if="referenceCounts">
            Betroffen: {{ referenceCounts.teams }} Mannschaft(en),
            {{ referenceCounts.games }} Spiel(e),
            {{ referenceCounts.gameDays }} Spieltag(e),
            {{ referenceCounts.preSeasonTeams }} Vor-Saison-Anmeldung(en).
          </p>
          <div v-if="selectedFieldRows.length > 0" style="margin-bottom: 16px">
            Folgende Angaben werden nach
            <strong>„{{ targetLocation.name }}"</strong> übertragen:
            <ul style="margin: 6px 0 0 16px">
              <li v-for="row in selectedFieldRows" :key="row.key">
                {{ row.label }}:
                <span v-if="row.targetEmpty" style="color: #6c757d">— </span>
                <span v-else><s>{{ row.targetText }}</s> </span>
                &rarr; <strong>{{ row.sourceText }}</strong>
              </li>
            </ul>
          </div>
          <p v-else style="color: #6c757d">
            Es werden keine Angaben übertragen; der behaltene Spielort bleibt
            unverändert.
          </p>
          <p>Soll fortgefahren werden?</p>
        </div>
        <template #footer>
          <Button
            label="Abbrechen"
            severity="secondary"
            icon="pi pi-times"
            :disabled="busy"
            @click="confirmDialog = false"
          />
          <Button
            label="Jetzt zusammenführen"
            icon="pi pi-link"
            severity="danger"
            :loading="busy"
            :disabled="busy"
            @click="merge"
          />
        </template>
      </Dialog>
    </div>
  </div>
</template>

<script>
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import { useMenuService } from "../../services/MenuService/UseMenuService";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Message from "primevue/message";
import Checkbox from "primevue/checkbox";

const TRANSFERABLE_FIELDS = [
  { key: "name", label: "Name" },
  { key: "street", label: "Straße" },
  { key: "plz", label: "PLZ" },
  { key: "city", label: "Ort" },
  { key: "numberOfBoards", label: "Board-Anzahl" },
  { key: "smokingType", label: "Raucher-Regelung" },
  { key: "youthAllowed", label: "Jugend erlaubt" },
  { key: "additionalInformations", label: "Zusatzinformationen" },
  { key: "coordinates", label: "Koordinaten" },
];

export default {
  name: "LocationMergeView",
  components: { Dropdown, Button, Dialog, Message, Checkbox },
  data() {
    return {
      locations: [],
      sourceLocation: null,
      targetLocation: null,
      referenceCounts: null,
      loadingCounts: false,
      busy: false,
      confirmDialog: false,
      error: "",
      filterFields: ["name", "street", "plz", "city"],
      duplicates: [],
      loadingDuplicates: false,
      showDuplicates: true,
      fieldsFromSource: [],
    };
  },
  computed: {
    sourceOptions() {
      if (!this.targetLocation) {
        return this.locations;
      }
      return this.locations.filter((l) => l.id !== this.targetLocation.id);
    },
    targetOptions() {
      if (!this.sourceLocation) {
        return this.locations;
      }
      return this.locations.filter((l) => l.id !== this.sourceLocation.id);
    },
    canMerge() {
      return (
        this.sourceLocation &&
        this.targetLocation &&
        this.sourceLocation.id !== this.targetLocation.id
      );
    },
    duplicateRows() {
      return this.duplicates.map((pair) => ({
        pair,
        keep: this.locationById(pair.keepId),
        remove: this.locationById(pair.removeId),
      }));
    },
    fieldRows() {
      if (!this.sourceLocation || !this.targetLocation) {
        return [];
      }
      return TRANSFERABLE_FIELDS.map((field) => {
        var sourceEmpty = !this.hasFieldValue(this.sourceLocation, field.key);
        var targetEmpty = !this.hasFieldValue(this.targetLocation, field.key);
        var equal =
          !sourceEmpty &&
          !targetEmpty &&
          this.fieldsEqual(this.sourceLocation, this.targetLocation, field.key);
        return {
          key: field.key,
          label: field.label,
          sourceEmpty: sourceEmpty,
          targetEmpty: targetEmpty,
          sourceText: this.fieldText(this.sourceLocation, field.key),
          targetText: this.fieldText(this.targetLocation, field.key),
          transferable: !sourceEmpty && !equal,
          conflict: !sourceEmpty && !targetEmpty && !equal,
        };
      });
    },
    selectedFieldRows() {
      return this.fieldRows.filter(
        (row) => row.transferable && this.fieldsFromSource.indexOf(row.key) >= 0
      );
    },
    coordinatesSelected() {
      return this.selectedFieldRows.some((row) => row.key === "coordinates");
    },
  },
  watch: {
    sourceLocation() {
      this.loadReferenceCounts();
      this.initFieldSelection();
    },
    targetLocation() {
      this.initFieldSelection();
    },
  },
  mounted() {
    const menuService = useMenuService();
    menuService.setMainMenuItemNames(["home"]);
    this.loadLocations();
  },
  methods: {
    locationById(id) {
      return this.locations.find((l) => l.id === id) || null;
    },
    scoreBadgeStyle(score) {
      if (score >= 90) return { background: "#b3261e" };
      if (score >= 80) return { background: "#e08600" };
      return { background: "#6c757d" };
    },
    smokingLabel(value) {
      if (value === "smoking") return "Raucher";
      if (value === "nonSmoking") return "Nichtraucher";
      if (value === "nonSmokingIfRequested") return "Nichtraucher auf Anfrage";
      return value;
    },
    hasCoordinates(location) {
      return !!location && !!location.latitute && !!location.longitude;
    },
    hasFieldValue(location, key) {
      if (!location) {
        return false;
      }
      if (key === "coordinates") {
        return this.hasCoordinates(location);
      }
      var value = location[key];
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === "string") {
        return value.trim().length > 0;
      }
      return true;
    },
    fieldsEqual(a, b, key) {
      if (key === "coordinates") {
        return a.latitute === b.latitute && a.longitude === b.longitude;
      }
      var valueA = a[key];
      var valueB = b[key];
      if (typeof valueA === "string" && typeof valueB === "string") {
        return valueA.trim() === valueB.trim();
      }
      return valueA === valueB;
    },
    fieldText(location, key) {
      if (!this.hasFieldValue(location, key)) {
        return "";
      }
      if (key === "coordinates") {
        return (
          Number(location.latitute).toFixed(5) +
          ", " +
          Number(location.longitude).toFixed(5)
        );
      }
      if (key === "smokingType") {
        return this.smokingLabel(location.smokingType);
      }
      if (key === "youthAllowed") {
        return location.youthAllowed ? "Ja" : "Nein";
      }
      return String(location[key]);
    },
    initFieldSelection() {
      this.fieldsFromSource = this.fieldRows
        .filter((row) => row.transferable && row.targetEmpty)
        .map((row) => row.key);
    },
    fetchLocations() {
      return ServerConnection.get("data/location").then((response) => {
        var list = Array.isArray(response.data) ? response.data : [];
        list.sort((a, b) =>
          (a.representation || a.name || "").localeCompare(
            b.representation || b.name || ""
          )
        );
        this.locations = list;
        return list;
      });
    },
    loadLocations() {
      this.fetchLocations()
        .then(() => {
          this.loadDuplicates();
        })
        .catch((err) => {
          this.error = "Konnte Spielorte nicht laden: " + this.formatError(err);
        });
    },
    reloadLocations(reselectTargetId) {
      this.fetchLocations()
        .then(() => {
          if (reselectTargetId) {
            this.targetLocation =
              this.locationById(reselectTargetId) || this.targetLocation;
          }
        })
        .catch((err) => {
          this.error =
            "Konnte Spielorte nicht neu laden: " + this.formatError(err);
        });
    },
    loadDuplicates() {
      this.loadingDuplicates = true;
      ServerConnection.get("admin/location/duplicates")
        .then((response) => {
          this.duplicates = Array.isArray(response.data) ? response.data : [];
        })
        .catch((err) => {
          this.error =
            "Konnte Dubletten nicht ermitteln: " + this.formatError(err);
        })
        .finally(() => {
          this.loadingDuplicates = false;
        });
    },
    applyDuplicate(pair) {
      if (this.busy) {
        return;
      }
      // erst leeren, damit die wechselseitige Options-Filterung nicht blockiert
      this.sourceLocation = null;
      this.targetLocation = this.locationById(pair.keepId);
      this.sourceLocation = this.locationById(pair.removeId);
      this.error = "";
    },
    swapLocations() {
      var previousSource = this.sourceLocation;
      this.sourceLocation = this.targetLocation;
      this.targetLocation = previousSource;
    },
    loadReferenceCounts() {
      this.referenceCounts = null;
      if (!this.sourceLocation || !this.sourceLocation.id) {
        return;
      }
      this.loadingCounts = true;
      var requestedId = this.sourceLocation.id;
      ServerConnection.get("admin/location/" + requestedId + "/references")
        .then((response) => {
          if (this.sourceLocation && this.sourceLocation.id === requestedId) {
            this.referenceCounts = response.data;
          }
        })
        .catch((err) => {
          if (this.sourceLocation && this.sourceLocation.id === requestedId) {
            this.error = "Konnte Auswirkung nicht ermitteln: " + this.formatError(err);
          }
        })
        .finally(() => {
          if (this.sourceLocation && this.sourceLocation.id === requestedId) {
            this.loadingCounts = false;
          }
        });
    },
    merge() {
      if (!this.canMerge || this.busy) {
        return;
      }
      this.busy = true;
      this.error = "";
      var sourceId = this.sourceLocation.id;
      var sourceName = this.sourceLocation.name;
      var targetId = this.targetLocation.id;
      var targetName = this.targetLocation.name;
      ServerConnection.post("admin/location/merge", {
        sourceId: sourceId,
        targetId: targetId,
        fieldsFromSource: this.selectedFieldRows.map((row) => row.key),
      })
        .then((response) => {
          var counts = response.data || {};
          var transferred = counts.transferredFields || [];
          this.busy = false;
          this.confirmDialog = false;
          this.$toast.add({
            severity: "success",
            summary: "Zusammengeführt",
            detail:
              '"' + sourceName + '" wurde in "' + targetName + '" überführt. ' +
              (counts.teams || 0) + " Mannschaft(en), " +
              (counts.games || 0) + " Spiel(e), " +
              (counts.gameDays || 0) + " Spieltag(e), " +
              (counts.preSeasonTeams || 0) + " Vor-Saison-Anmeldung(en) angepasst." +
              (transferred.length > 0
                ? " Übernommen: " + transferred.join(", ") + "."
                : ""),
            life: 6000,
          });
          // Liste neu laden, gemergten entfernen, Auswahl zurücksetzen
          this.locations = this.locations.filter((l) => l.id !== sourceId);
          this.duplicates = this.duplicates.filter(
            (p) => p.keepId !== sourceId && p.removeId !== sourceId
          );
          this.sourceLocation = null;
          this.referenceCounts = null;
          if (transferred.length > 0) {
            this.reloadLocations(targetId);
          }
        })
        .catch((err) => {
          this.busy = false;
          this.error = "Zusammenführen fehlgeschlagen: " + this.formatError(err);
        });
    },
    reset() {
      this.sourceLocation = null;
      this.targetLocation = null;
      this.referenceCounts = null;
      this.fieldsFromSource = [];
      this.error = "";
    },
    formatError(error) {
      if (error && error.response && error.response.data) {
        var data = error.response.data;
        if (typeof data === "string" && data.length > 0) {
          return data;
        }
      }
      if (error && error.message) {
        return error.message;
      }
      return "Unbekannter Fehler";
    },
  },
};
</script>
